import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslate } from "@tolgee/react";
import toast from "react-hot-toast";
import {
  useClockOutMutation,
  useVisitByIdQuery,
} from "../../api/generated/graphql";
import { useGeoLocation } from "../../hooks/geoLocation";
import { careCompleted } from "../../typewriter/segment";
import { ThankYouSlideOver } from "./ThankYouSlideOver";
import { useVisitInfo } from "../../utils/visits";
import { attemptToStartSentryReplay } from "../../utils/sentryUtils";
import { useFeatureFlag } from "../../providers/FeatureFlags";

import { VisitNoteForm } from "../../components/shared";

type FormValues = {
  visitNote: string;
};

export const ClockOut = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslate();
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const { data } = useVisitByIdQuery({
    variables: { visitInstanceId: id ?? "" },
  });

  const navigate = useNavigate();
  const [clockOutMutation] = useClockOutMutation();
  const [clockOutLoading, setClockOutLoading] = useState(false);
  const ff_signatures = useFeatureFlag("Signatures");

  const { getPosition } = useGeoLocation();

  useEffect(() => {
    attemptToStartSentryReplay();
  }, []);

  const subject = data?.visitById?.subject;
  const info = useVisitInfo(data?.visitById);

  // TODO: Handle case when subject is not care recipient
  if (!subject || subject.__typename !== "CareRecipient") {
    return <>Error: Visit does not belong to a CareRecipient</>;
  }
  const clockOutTime =
    searchParams.get("scheduled") !== "true"
      ? new Date().toISOString()
      : (info?.to?.toString() ?? "");

  const totalExpenses = localStorage.getItem("expenses|" + id);
  const milesDrivenToAndFrom = localStorage.getItem(
    "milesDrivenToAndFromLocation|" + id,
  );
  const milesDrivenForClient = localStorage.getItem(
    "milesDrivenForClient|" + id,
  );
  const expensesReason = localStorage.getItem("expenseReason|" + id);

  const onSubmit = async (values: FormValues) => {
    Sentry.setTag("clockOutButtonClicked", true);

    Sentry.addBreadcrumb({
      category: "ui.click",
      message: "User attempting to clock out",
      data: {
        visitId: id,
      },
    });

    if (ff_signatures) {
      localStorage.removeItem("caregiverSignature");
      localStorage.removeItem("careRecipientSignature");
    }

    setClockOutLoading(true);

    const position = await getPosition();
    const coords = position?.coords;
    const location =
      coords?.latitude && coords.longitude
        ? {
            lat: coords.latitude,
            lng: coords.longitude,
          }
        : undefined;

    const input = {
      visitNote: values.visitNote,
      expenses: [
        {
          reportedAmount: totalExpenses,
          description: expensesReason ?? "",
        },
      ],
      mileage: {
        drivenToLocationReported: milesDrivenToAndFrom,
        drivenForCareRecipientReported: milesDrivenForClient,
      },
      visitInstanceId: id ?? "",
      clockOutLocation: location,
      clockOutTime,
    };

    Sentry.addBreadcrumb({
      category: "ui.click",
      message: "User clocking out",
      data: {
        visitId: id,
      },
    });

    await clockOutMutation({
      variables: { input },
      refetchQueries: ["VisitById"],
    }).catch(() => {
      Sentry.addBreadcrumb({
        category: "ui.error",
        message: "User failed to clock out",
        data: {
          visitId: id,
        },
      });
      toast.error(t("clockout.error"));
    });

    localStorage.removeItem("visitNote|" + id);
    localStorage.removeItem("expenses|" + id);
    localStorage.removeItem("milesDrivenToAndFromLocation|" + id);
    localStorage.removeItem("milesDrivenForClient|" + id);
    localStorage.removeItem("expenseReason|" + id);

    Sentry.addBreadcrumb({
      category: "ui.success",
      message: "User shown the thank you modal",
      data: {
        visitId: id,
      },
    });

    setShowThankYouModal(true);
    careCompleted({});
    setClockOutLoading(false);
  };

  return (
    <>
      <VisitNoteForm
        visitId={id ?? ""}
        onSubmit={onSubmit}
        buttonText={t("submit")}
        loading={clockOutLoading}
      />
      {showThankYouModal && (
        <ThankYouSlideOver
          name={subject.firstName ?? ""}
          onDone={() => {
            navigate("/");
            Sentry.addBreadcrumb({
              category: "ui.success",
              message: "User closed the thank you modal",
            });
          }}
        />
      )}
    </>
  );
};
